/**
 * Prototype E: the expedition. One vertical world, one land per week.
 *
 * Each land opens with a unit header in its own colour, then the week's main
 * items step down the land as chunky coins with no road between them: the
 * rhythm of the steps is the path, as on Duolingo. The ground carries a faint
 * isometric checker like chess.com's board, and the scenery keeps natural
 * colours (green foliage, brown wood, blue water, white daisies) so every land
 * holds several hues. Optional items sit on a "Going further" shelf at the end
 * of the land. Lands after the current week keep their palette but fade and
 * lock. The current coin gets a progress ring and a START bubble; the mascot
 * waits beside it.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {FaRocket} from 'react-icons/fa';
import styled from 'styled-components';
import type {PlushState} from '../mascot/PaiPlush';
import {
  CheckIcon,
  CodeIcon,
  GemIcon,
  LearnIcon,
  LinkIcon,
  LockIcon,
  SectionIcon,
  TrophyIcon,
} from './journeyIcons';
import {JourneyStone, LOCKED_STONE_COLOR} from './JourneyStone';
import {darken, lighten, mix} from './mapColor';
import {
  HtmlDecorations,
  mapCanvasFilterCss,
  svgDecorations,
  type LandBox,
  type RoadmapLayoutInfo,
} from './lookAndFeel/decorations';
import {
  DEFAULT_LOOK_AND_FEEL,
  weekLook,
  type LookAndFeel,
  type Placement,
} from './lookAndFeel/lookAndFeelSchema';
import {FINALE_HEIGHT, MapFinale} from './MapFinale';
import {DEFAULT_PALETTE, defaultGround, defaultRoad} from './palettes';
import {
  meanderPath,
  meanderX,
  roadX,
  type Meander,
  type MeanderSkip,
  type RoadAnchor,
} from './meander';
import type {
  ExpeditionItem,
  ExpeditionItemKind,
  ExpeditionWeek,
} from './expeditionModel';
import {findNextItem, itemTooltip} from './expeditionModel';
import {allDone, countDone} from './roadmapPrototypeShared';
import {useMapLinkCallout} from './useMapLinkCallout';

/**
 * The phone canvas. Every horizontal position on the map is a fraction of
 * the canvas width, so a wider canvas is this one stretched sideways: the
 * road swings wider and the margins grow while heights, text and coins keep
 * their size. The canvas grows with its container up to
 * {@link MAX_CANVAS_WIDTH}; containers narrower than this show the whole
 * canvas scaled down.
 */
const CANVAS_WIDTH = 430;
/** The widest the canvas stretches on a large screen. */
const MAX_CANVAS_WIDTH = 640;
/** Containers narrower than this get the canvas edge to edge. */
const FULL_BLEED_BELOW = CANVAS_WIDTH + 24;
const STONE_SIZE = 72;
/** Gap between the current coin and its progress ring, mirrored in JourneyStone. */
const RING_GAP = 9;
const GEM_SIZE = 48;
const GEM_PITCH = 100;
const CAPTION_WIDTH = 150;
const GEM_CAPTION_HEIGHT = 62;
const HEADER_MARGIN_X = 22;
const HEADER_HEIGHT = 92;
const HEADER_TO_FIRST_STONE = 96;
/** Room a caption takes beneath its coin. */
const UNDER_CAPTION = 74;
/** Extra room above the current coin so the START bubble clears the coin before it. */
const BUBBLE_ROOM = 54;
const SHELF_MIN_WIDTH = 220;
const SHELF_LABEL_HEIGHT = 22;
const SHELF_PADDING = 16;
const LAND_TAIL = 60;
const HORIZON_AMPLITUDE = 14;
const PATH_WIDTH = 22;

/** A land's three colours. Everything else on the land is derived from them. */
interface Palette {
  /** Banner, coins and, darkened, the captions. */
  accent: string;
  /** The land's ground tint. */
  ground: string;
  /** The road's base colour; its edge and worn stretch lean toward the accent. */
  road: string;
}

/**
 * Fades a land's palette for a locked week: still recognisably its own
 * colour, but washed out so the eye reads it as "not yet".
 *
 * @param palette - The land's live palette.
 * @return The faded palette.
 */
function lockedPalette(palette: Palette): Palette {
  return {
    ground: mix(palette.ground, '#e8e4de', 0.55),
    accent: mix(palette.accent, '#b5ada3', 0.6),
    road: mix(palette.road, '#ddd6cd', 0.5),
  };
}

interface Point {
  x: number;
  y: number;
}

interface StoneLayout extends Point {
  item: ExpeditionItem;
}

interface ShelfLayout {
  left: number;
  top: number;
  width: number;
  height: number;
  gems: StoneLayout[];
}

interface LandLayout {
  g: Geometry;
  /** The land's title as shown: the week's, or the course's override. */
  title: string;
  /** Coins moved off the meander, which the road detours through. */
  anchors: RoadAnchor[];
  /** Where the road enters and leaves the land, as canvas y. */
  roadFrom: number;
  roadTo: number;
  week: ExpeditionWeek;
  index: number;
  /** The colours drawn: faded when the land is locked. */
  palette: Palette;
  /** The colours the week has, before any locked fade; what an editor edits. */
  livePalette: Palette;
  isLocked: boolean;
  top: number;
  bottom: number;
  headerTop: number;
  stones: StoneLayout[];
  shelf?: ShelfLayout;
}

interface ExpeditionLayout {
  g: Geometry;
  /** The road's shape, with the phase frozen between lands. */
  meander: Meander;
  height: number;
  lands: LandLayout[];
  /** Top edge of the closing card. */
  finaleTop: number;
}

/** The numbers every layout step reads. */
interface Geometry {
  width: number;
  centerX: number;
  /** How far coins swing left and right of the centre line. */
  amplitude: number;
  /** Horizontal room outside the coins and their captions, per side. */
  marginWidth: number;
  /** Vertical distance between consecutive coins. */
  stepY: number;
  /** The road's shape on this canvas. */
  meander: Meander;
}

/** How far coins swing left and right of the centre line. */
const AMPLITUDE = 60;

/**
 * The layout geometry for a canvas width. The swing of the road scales with
 * the width so a wider canvas is the phone canvas stretched sideways.
 *
 * @param width - Canvas width in pixels, between {@link CANVAS_WIDTH} and
 *     {@link MAX_CANVAS_WIDTH}.
 * @return The geometry.
 */
function geometryFor(width: number): Geometry {
  const amplitude = (AMPLITUDE * width) / CANVAS_WIDTH;
  return {
    width,
    centerX: width / 2,
    amplitude,
    marginWidth: width / 2 - amplitude - CAPTION_WIDTH / 2,
    stepY: 172,
    meander: {centerX: width / 2, amplitude},
  };
}

/**
 * Lays the world out in pixel space.
 *
 * @param args.weeks - Weeks, top to bottom.
 * @param args.nextItemId - The coin that carries the START bubble, which
 *     needs extra room above it. When undefined, no coin gets extra room.
 * @param args.g - The geometry for the available width.
 * @return Positioned lands and the total canvas height.
 */
function layoutExpedition({
  weeks,
  nextItemId,
  g,
  lookAndFeel,
}: {
  weeks: readonly ExpeditionWeek[];
  nextItemId: string | undefined;
  g: Geometry;
  lookAndFeel: LookAndFeel;
}): ExpeditionLayout {
  const lands: LandLayout[] = [];
  let cursor = 0;
  // The wave does not advance between one land's road and the next's, so
  // each land's road resumes where the previous one stopped.
  const skips: MeanderSkip[] = [];
  const meander: Meander = {...g.meander, skips};
  let previousRoadTo: number | undefined;

  weeks.forEach((week, index) => {
    const isLocked = week.isLocked;
    const look = weekLook(lookAndFeel, week.id);
    const top = cursor;
    const headerTop = top + 26;
    let y = headerTop + HEADER_HEIGHT + HEADER_TO_FIRST_STONE;
    // The road emerges from under the banner, which is drawn above it.
    const roadFrom = headerTop + HEADER_HEIGHT - 14;
    if (previousRoadTo !== undefined && roadFrom > previousRoadTo) {
      skips.push({from: previousRoadTo, to: roadFrom});
    }

    const stones: StoneLayout[] = [];
    let contentBottom = y - STONE_SIZE / 2;
    // First the heights, so moved coins can pull the road; then every coin
    // takes its x from the road so unmoved ones follow the detours too.
    const slots: Array<{item: ExpeditionItem; y: number}> = [];
    week.main.forEach(item => {
      if (item.id === nextItemId) {
        y += BUBBLE_ROOM;
      }
      const nudge = look.stones[item.id]?.dy ?? 0;
      slots.push({item, y: y + nudge});
      contentBottom = Math.max(
        contentBottom,
        y + nudge + STONE_SIZE / 2 + UNDER_CAPTION,
      );
      y += g.stepY;
    });
    // Every coin is an anchor; only moved ones pull, so the road's offset
    // eases from coin to coin and returns to the meander at unmoved ones.
    const anchors: RoadAnchor[] = look.stones
      ? slots.map(({item, y: slotY}) => {
          const moved = look.stones[item.id];
          return {
            y: slotY,
            dx: moved ? moved.x * g.width - meanderX(slotY, meander) : 0,
            reach: g.stepY * 0.9,
          };
        })
      : [];
    slots.forEach(({item, y: slotY}) => {
      stones.push({item, x: roadX(slotY, meander, anchors), y: slotY});
    });
    const lastStoneY = stones.length > 0 ? stones[stones.length - 1].y : y;

    let shelf: ShelfLayout | undefined;
    if (week.goingFurther.length > 0) {
      const count = week.goingFurther.length;
      const maxWidth = g.width - HEADER_MARGIN_X * 2;
      const perRow = Math.max(
        1,
        Math.floor((maxWidth - SHELF_PADDING * 2) / GEM_PITCH),
      );
      const rows = Math.ceil(count / perRow);
      const width = Math.min(
        maxWidth,
        Math.max(
          SHELF_MIN_WIDTH,
          Math.min(count, perRow) * GEM_PITCH + SHELF_PADDING * 2,
        ),
      );
      const rowHeight = GEM_SIZE + GEM_CAPTION_HEIGHT;
      const height =
        SHELF_PADDING +
        SHELF_LABEL_HEIGHT +
        12 +
        rows * rowHeight +
        SHELF_PADDING;
      const left = g.centerX - width / 2;
      const shelfTop = contentBottom + 40;
      const gems = week.goingFurther.map((item, gemIndex) => {
        const row = Math.floor(gemIndex / perRow);
        const column = gemIndex % perRow;
        const inRow = Math.min(perRow, count - row * perRow);
        const rowLeft = g.centerX - (inRow * GEM_PITCH) / 2;
        return {
          item,
          x: rowLeft + GEM_PITCH * column + GEM_PITCH / 2,
          y:
            shelfTop +
            SHELF_PADDING +
            SHELF_LABEL_HEIGHT +
            12 +
            row * rowHeight +
            GEM_SIZE / 2,
        };
      });
      shelf = {left, top: shelfTop, width, height, gems};
      contentBottom = shelfTop + height;
    }

    const bottom = contentBottom + LAND_TAIL;
    const stock = DEFAULT_PALETTE.lands[index % DEFAULT_PALETTE.lands.length];
    // A week may set any of its three colours; the rest follow the accent so
    // the land stays coherent.
    const accent = look.accent ?? stock.accent;
    const ground =
      look.ground ?? (look.accent ? defaultGround(look.accent) : stock.ground);
    const palette: Palette = {
      accent,
      ground,
      road: look.road ?? defaultRoad(ground, accent),
    };
    const roadTo = Math.min(
      lastStoneY + STONE_SIZE / 2 + 48,
      (shelf?.top ?? Number.POSITIVE_INFINITY) - 16,
    );
    previousRoadTo = Math.max(roadFrom, roadTo);
    lands.push({
      g,
      title: look.title?.trim() || week.title,
      anchors,
      roadFrom,
      roadTo,
      week,
      index,
      palette: isLocked ? lockedPalette(palette) : palette,
      livePalette: palette,
      isLocked,
      top,
      bottom,
      headerTop,
      stones,
      shelf,
    });
    cursor = bottom;
  });

  const finaleTop = cursor + 12;
  const height = lookAndFeel.showFinale
    ? finaleTop + FINALE_HEIGHT + 28
    : cursor + 24;
  return {g, meander, height, lands, finaleTop};
}

/**
 * Automatic placements the course has adopted or removed are not drawn.
 *
 * @param placements - Automatic placements.
 * @param hiddenAuto - Ids the week hides.
 * @return The placements still to draw.
 */
function withoutHidden(
  placements: Placement[],
  hiddenAuto: readonly string[],
): Placement[] {
  return hiddenAuto.length === 0
    ? placements
    : placements.filter(placement => !hiddenAuto.includes(placement.id));
}

/**
 * The public shape of a land, for decorations and for editors overlaying
 * the map.
 *
 * @param land - The laid-out land.
 * @return Its box and colours.
 */
function landBox(land: LandLayout, autoPlacements: Placement[]): LandBox {
  return {
    id: land.week.id,
    top: land.top,
    contentTop: landContentTop(land),
    bottom: land.bottom,
    accent: land.livePalette.accent,
    ground: land.livePalette.ground,
    title: land.title,
    stones: land.stones.map(stone => ({
      id: stone.item.id,
      x: stone.x,
      y: stone.y,
    })),
    autoPlacements,
  };
}

/**
 * The expression the land's plush wears for a viewer's progress.
 *
 * @param land - The land.
 * @param completed - Completed item ids.
 * @param nextItemId - The viewer's next item, if any.
 * @return The plush state.
 */
function plushStateFor(
  land: LandLayout,
  completed: ReadonlySet<string>,
  nextItemId: string | undefined,
): PlushState {
  if (land.isLocked) {
    return 'sleepy';
  }
  if (allDone(land.week.main, completed)) {
    return 'happy';
  }
  return land.stones.some(stone => stone.item.id === nextItemId)
    ? 'surprised'
    : 'idle';
}

/**
 * Everything the map draws on its own for a land, as placements.
 *
 * @param land - The land.
 * @param completed - Completed item ids.
 * @param nextItemId - The viewer's next item, if any.
 * @return Scenery first, then the plush.
 */
function autoPlacementsFor(
  land: LandLayout,
  completed: ReadonlySet<string>,
  nextItemId: string | undefined,
): Placement[] {
  const side = plushSideOf(land);
  return [
    ...autoSceneryPlacements(land, side),
    ...autoMascotPlacements(land, completed, nextItemId),
  ];
}

/**
 * The mascots the map seats on its own: one in the margin and, on the land
 * holding the next coin, Beanie beside that coin.
 *
 * @param land - The land.
 * @param completed - Ids of completed items.
 * @param nextItemId - The coin up next, if any.
 * @return Placements with ids stable across renders.
 */
function autoMascotPlacements(
  land: LandLayout,
  completed: ReadonlySet<string>,
  nextItemId: string | undefined,
): Placement[] {
  return [
    ...autoPlushPlacement(land, plushStateFor(land, completed, nextItemId)),
    ...autoCompanionPlacement(land, nextItemId),
  ];
}

/**
 * Beanie beside the next coin, on the side away from its caption's lean.
 *
 * @param land - The land.
 * @param nextItemId - The coin up next, if any.
 * @return One placement when the next coin is on this land, else none.
 */
function autoCompanionPlacement(
  land: LandLayout,
  nextItemId: string | undefined,
): Placement[] {
  const stone = land.stones.find(candidate => candidate.item.id === nextItemId);
  if (!stone) {
    return [];
  }
  const side = stone.x >= land.g.centerX ? 1 : -1;
  return [
    {
      id: `auto-${land.week.id}-companion`,
      widget: 'beanie',
      ...fractionsOn(
        land,
        stone.x - side * (STONE_SIZE / 2 + 44),
        stone.y + STONE_SIZE / 2 + 2,
      ),
      scale: 1,
      flip: false,
      variant: 'happy',
    },
  ];
}

/**
 * The land's shape: a rectangle whose bottom edge is a gentle wave.
 *
 * @param land - The land to outline.
 * @param isLast - The last land keeps a flat bottom.
 * @return An SVG path string.
 */
function landPath(land: LandLayout, isLast: boolean): string {
  const {top, bottom} = land;
  if (isLast) {
    return `M 0 ${top} H ${land.g.width} V ${bottom + 40} H 0 Z`;
  }
  const a = HORIZON_AMPLITUDE;
  const phase = land.index % 2 === 0 ? 1 : -1;
  const WIDTH = land.g.width;
  return [
    `M 0 ${top}`,
    `H ${WIDTH}`,
    `V ${bottom - phase * a}`,
    `C ${WIDTH * 0.8} ${bottom - phase * a * 2.4}, ${WIDTH * 0.62} ${bottom + phase * a * 1.6}, ${WIDTH * 0.48} ${bottom + phase * a * 0.4}`,
    `C ${WIDTH * 0.34} ${bottom - phase * a * 1.2}, ${WIDTH * 0.16} ${bottom + phase * a * 2}, 0 ${bottom + phase * a * 0.6}`,
    'Z',
  ].join(' ');
}

/**
 * One of the soft ridges along a land's horizon. Ridge 0 is the farthest
 * and tallest; each nearer ridge sits lower so three layers read as depth.
 *
 * @param land - The land.
 * @param ridge - 0, 1 or 2, farthest to nearest.
 * @return An SVG path string, closed below the horizon.
 */
function ridgePath(land: LandLayout, ridge: number): string {
  const base = land.bottom + 30;
  const rise = [118, 84, 52][ridge] ?? 52;
  const shift = [0.18, 0.62, 0.4][ridge] ?? 0.5;
  const WIDTH = land.g.width;
  const peakX = WIDTH * shift;
  const top = base - rise;
  return [
    `M -20 ${base}`,
    `C ${peakX - 240} ${base}, ${peakX - 150} ${top}, ${peakX} ${top}`,
    `C ${peakX + 150} ${top}, ${peakX + 240} ${base}, ${WIDTH + 20} ${base}`,
    'Z',
  ].join(' ');
}

/**
 * Ground lines in a margin that no side caption or shelf occupies.
 *
 * A caption sits beside its coin on the side away from the centre line, so
 * the left margin is busy wherever a coin on the left has its caption, and
 * likewise on the right. The shelf spans nearly the full width, so both
 * margins are busy across it.
 *
 * @param land - The land to search.
 * @param side - Which margin: -1 for left, 1 for right.
 * @return Candidate ground y positions, top to bottom.
 */
function freeGroundLines(land: LandLayout, side: 1 | -1): number[] {
  const contentTop = land.headerTop + HEADER_HEIGHT + 20;
  const contentBottom = land.bottom - 10;
  const busy: Array<[number, number]> = land.stones
    .filter(stone => (stone.x >= land.g.centerX ? 1 : -1) === side)
    .map(stone => [stone.y - 46, stone.y + 48]);
  if (land.shelf) {
    busy.push([land.shelf.top - 10, land.shelf.top + land.shelf.height + 10]);
  }
  const lines: number[] = [];
  // A prop stands on its ground line and rises about 70px above it.
  for (let y = contentTop + 70; y <= contentBottom; y += 20) {
    const isFree = busy.every(([from, to]) => y < from || y - 70 > to);
    if (isFree) {
      lines.push(y);
    }
  }
  return lines;
}

/** Minimum vertical distance between two props sharing a margin. */
const PROP_MIN_GAP = 118;

/**
 * Picks well-spread ground lines from the free ones.
 *
 * Free lines come 20px apart, so they are first thinned to at least
 * {@link PROP_MIN_GAP} apart; otherwise two props would stand on top of each
 * other whenever a margin has little room.
 *
 * @param lines - Free ground lines, top to bottom.
 * @param count - How many to pick.
 * @return Up to `count` lines, spread across the range.
 */
function spread(lines: number[], count: number): number[] {
  const thinned: number[] = [];
  for (const y of lines) {
    if (
      thinned.length === 0 ||
      y - thinned[thinned.length - 1] >= PROP_MIN_GAP
    ) {
      thinned.push(y);
    }
  }
  if (thinned.length === 0 || count === 0) {
    return [];
  }
  if (thinned.length <= count) {
    return thinned;
  }
  return Array.from(
    {length: count},
    (_, index) =>
      thinned[Math.round(((index + 0.5) * (thinned.length - 1)) / count)],
  );
}

/**
 * How a margin's free ground lines are shared between scenery and the plush.
 * The plush takes the lowest line on its side; scenery gets the rest.
 *
 * @param land - The land.
 * @param side - Which margin: -1 for left, 1 for right.
 * @param plushSide - The margin the plush sits in.
 * @return Scenery lines and, on the plush's side, the plush's line.
 */
function marginSlots(
  land: LandLayout,
  side: 1 | -1,
  plushSide: 1 | -1,
): {sceneryLines: number[]; plushLine?: number} {
  const lines = spread(freeGroundLines(land, side), 3);
  if (side !== plushSide || lines.length === 0) {
    return {sceneryLines: lines};
  }
  return {sceneryLines: lines.slice(0, -1), plushLine: lines[lines.length - 1]};
}

/**
 * Which margin a land's plush sits in: whichever has more room, since a
 * margin beside a run of coins is full of their captions. Ties alternate.
 *
 * @param land - The land.
 * @return -1 for the left margin, 1 for the right.
 */
function plushSideOf(land: LandLayout): 1 | -1 {
  const left = spread(freeGroundLines(land, -1), 3).length;
  const right = spread(freeGroundLines(land, 1), 3).length;
  if (left === right) {
    return land.index % 2 === 0 ? 1 : -1;
  }
  return left > right ? -1 : 1;
}

/**
 * Where a land's plush stands: the third free ground line on its side, the
 * slot the scenery leaves for it.
 *
 * @param land - The land.
 * @return The plush's base point, or undefined when the margin has no room.
 */
function plushSpot(land: LandLayout): Point | undefined {
  if (land.g.marginWidth < 60) {
    return undefined;
  }
  const side = plushSideOf(land);
  const {plushLine} = marginSlots(land, side, side);
  if (plushLine === undefined) {
    return undefined;
  }
  const inset = land.g.marginWidth / 2 + 4;
  return {x: side === 1 ? land.g.width - inset : inset, y: plushLine};
}

/** The top of a land's placeable area, just under its header. */
function landContentTop(land: LandLayout): number {
  return land.headerTop + HEADER_HEIGHT + 8;
}

/**
 * Converts a canvas point on a land to placement fractions.
 *
 * @param land - The land.
 * @param x - Canvas x in pixels.
 * @param y - Canvas y in pixels.
 * @return Fractions of the canvas width and the land's content height.
 */
function fractionsOn(
  land: LandLayout,
  x: number,
  y: number,
): {x: number; y: number} {
  const top = landContentTop(land);
  return {
    x: x / land.g.width,
    y: Math.min(1, Math.max(0, (y - top) / Math.max(1, land.bottom - top))),
  };
}

/**
 * The scenery the map places on its own in a land's margins, as widget
 * placements, so it renders through the same path as an editor's widgets
 * and can be adopted into the document.
 *
 * @param land - The land to decorate.
 * @param plushSide - The margin reserved for the plush.
 * @return Placements with ids stable across renders.
 */
function autoSceneryPlacements(
  land: LandLayout,
  plushSide: 1 | -1,
): Placement[] {
  const {g} = land;
  if (g.marginWidth < 60) {
    return [];
  }
  const variant = land.index % 3;
  const left = marginSlots(land, -1, plushSide).sceneryLines;
  const right = marginSlots(land, 1, plushSide).sceneryLines;
  const sc = 0.9;
  const L = g.marginWidth / 2;
  const R = g.width - L;
  const leftProps: Array<[string, number, number]> = [
    variant === 1 ? ['conifer', L - 2, 1.1 * sc] : ['broad-tree', L, 1.05 * sc],
    variant === 2 ? ['pond', L + 18, 1 * sc] : ['shrub', L + 16, 1.1 * sc],
    variant === 0 ? ['conifer', L + 6, 0.95 * sc] : ['shrub', L + 10, 1 * sc],
  ];
  const rightProps: Array<[string, number, number]> = [
    variant === 0
      ? ['conifer', R + 2, 1.15 * sc]
      : ['broad-tree', R - 2, 1.05 * sc],
    variant === 1
      ? ['pond', R - 18, 1.05 * sc]
      : variant === 2
        ? ['signpost', R - 6, 1 * sc]
        : ['shrub', R - 14, 1.1 * sc],
    variant === 2
      ? ['conifer', R + 6, 1 * sc]
      : ['broad-tree', R + 2, 0.85 * sc],
  ];
  const leftExtras: Array<[string, number, number]> = [
    ['daisies', L + 30, 1],
    ['rocks', L - 24, 0.9],
    ['daisies', L + 26, 0.9],
  ];
  const rightExtras: Array<[string, number, number]> = [
    ['rocks', R - 28, 0.9],
    ['daisies', R + 22, 1],
    ['rocks', R - 24, 0.8],
  ];
  const placements: Placement[] = [];
  const add = (
    side: string,
    index: number,
    prop: [string, number, number] | undefined,
    y: number,
    tag: string,
  ) => {
    if (!prop) {
      return;
    }
    const [widget, x, scale] = prop;
    placements.push({
      id: `auto-${land.week.id}-${side}${index}${tag}`,
      widget,
      ...fractionsOn(land, x, y),
      scale: Number(scale.toFixed(2)),
      flip: false,
    });
  };
  left.forEach((y, index) => {
    add('l', index, leftProps[index], y, '');
    add('l', index, leftExtras[index], y, 'x');
  });
  right.forEach((y, index) => {
    add('r', index, rightProps[index], y, '');
    add('r', index, rightExtras[index], y, 'x');
  });
  return placements;
}

/**
 * The plush the map seats in a land's margin, as a placement.
 *
 * @param land - The land.
 * @param state - The expression to show.
 * @return One placement, or none when the margin has no room.
 */
function autoPlushPlacement(land: LandLayout, state: PlushState): Placement[] {
  const spot = plushSpot(land);
  if (!spot) {
    return [];
  }
  const widget = ['ollie', 'plush-pi', 'beanie'][land.index % 3];
  return [
    {
      id: `auto-${land.week.id}-plush`,
      widget,
      ...fractionsOn(land, spot.x, spot.y + 6),
      scale: widget === 'beanie' ? 1 : 1.05,
      flip: false,
      variant: state,
    },
  ];
}

const Scroller = styled.div`
  overflow-x: hidden;
  padding-bottom: 1rem;
  /* Runs edge to edge inside the course page's narrow-screen gutter. */
  margin-left: calc(-1 * var(--course-page-gutter, 0px));
  margin-right: calc(-1 * var(--course-page-gutter, 0px));
`;

/** The canvas's footprint on screen: its logical size times the scale. */
const Frame = styled.div<{
  $width: number;
  $height: number;
  $isFullBleed: boolean;
}>`
  position: relative;
  width: ${({$width}) => $width}px;
  height: ${({$height}) => $height}px;
  margin: 0 auto;
  border-radius: ${({$isFullBleed}) => ($isFullBleed ? '0' : '24px')};
  overflow: hidden;
  box-shadow: ${({$isFullBleed}) =>
    $isFullBleed
      ? 'none'
      : '0 0 0 1px rgba(60, 40, 20, 0.06), 0 24px 48px rgba(60, 40, 20, 0.1)'};
`;

const Canvas = styled.div<{$width: number; $height: number; $scale: number}>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({$width}) => $width}px;
  height: ${({$height}) => $height}px;
  transform: scale(${({$scale}) => $scale});
  transform-origin: top left;
  background: #f2efe8;
  font-family: 'Source Sans 3', system-ui, sans-serif;
  ${mapCanvasFilterCss}
`;

const World = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const Header = styled.header<{$top: number; $accent: string}>`
  position: absolute;
  left: ${HEADER_MARGIN_X}px;
  right: ${HEADER_MARGIN_X}px;
  top: ${({$top}) => $top}px;
  height: ${HEADER_HEIGHT}px;
  box-sizing: border-box;
  border-radius: 20px;
  padding: 16px 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  color: #fff;
  background: linear-gradient(
    135deg,
    ${({$accent}) => lighten($accent, 0.08)} 0%,
    ${({$accent}) => $accent} 60%,
    ${({$accent}) => darken($accent, 0.1)} 100%
  );
  box-shadow:
    0 3px 0 ${({$accent}) => darken($accent, 0.28)},
    0 14px 28px rgba(40, 20, 10, 0.14);
`;

const HeaderKicker = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.85;
`;

const HeaderTitle = styled.h3`
  margin: 2px 0 0;
  font-family: 'Jost', system-ui, sans-serif;
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1.15;
  color: #fff;
`;

const HeaderPill = styled.div`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.9rem;
  background: rgba(255, 255, 255, 0.22);
  border: 1.5px solid rgba(255, 255, 255, 0.45);

  svg {
    width: 16px;
    height: 16px;
  }
`;

/** A coin's name, set beside it on the side away from the centre line. */
/** A coin's name beneath it, for the compact layout. */
const CenterCaption = styled.div<{$x: number; $y: number; $color: string}>`
  position: absolute;
  left: ${({$x}) => $x - CAPTION_WIDTH / 2}px;
  top: ${({$y}) => $y}px;
  width: ${CAPTION_WIDTH}px;
  text-align: center;
  line-height: 1.2;
  color: ${({$color}) => $color};
  pointer-events: none;

  small {
    display: block;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0.75;
    margin-bottom: 2px;
  }

  strong {
    display: block;
    font-size: 0.88rem;
    font-weight: 600;
  }
`;

/** A gem's name, centred beneath it on the shelf. */
const UnderCaption = styled.div<{$x: number; $y: number; $color: string}>`
  position: absolute;
  left: ${({$x}) => $x - (GEM_PITCH - 8) / 2}px;
  top: ${({$y}) => $y}px;
  width: ${GEM_PITCH - 8}px;
  text-align: center;
  line-height: 1.2;
  font-size: 0.76rem;
  font-weight: 600;
  color: ${({$color}) => $color};
  pointer-events: none;
`;

const Shelf = styled.section<{
  $left: number;
  $top: number;
  $width: number;
  $height: number;
  $accent: string;
  $ground: string;
}>`
  position: absolute;
  left: ${({$left}) => $left}px;
  top: ${({$top}) => $top}px;
  width: ${({$width}) => $width}px;
  height: ${({$height}) => $height}px;
  box-sizing: border-box;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.55);
  border: 2px dashed ${({$accent, $ground}) => mix($accent, $ground, 0.3)};
  box-shadow: 0 4px 0 rgba(0, 0, 0, 0.05);
`;

const ShelfLabel = styled.div<{$color: string}>`
  position: absolute;
  left: 0;
  right: 0;
  top: ${SHELF_PADDING}px;
  height: ${SHELF_LABEL_HEIGHT}px;
  text-align: center;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${({$color}) => $color};
`;

/** The Duolingo-style bubble above the current coin. */
const StartBubble = styled.div<{$x: number; $y: number; $accent: string}>`
  position: absolute;
  left: ${({$x}) => $x}px;
  top: ${({$y}) => $y}px;
  transform: translate(-50%, 0);
  padding: 8px 18px;
  border-radius: 14px;
  background: #fff;
  color: ${({$accent}) => $accent};
  font-weight: 800;
  font-size: 0.85rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: 2px solid ${({$accent}) => mix($accent, '#ffffff', 0.7)};
  box-shadow:
    0 3px 0 ${({$accent}) => mix($accent, '#ffffff', 0.7)},
    0 8px 18px rgba(40, 20, 10, 0.14);
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -8px;
    width: 14px;
    height: 14px;
    background: #fff;
    border-right: 2px solid ${({$accent}) => mix($accent, '#ffffff', 0.7)};
    border-bottom: 2px solid ${({$accent}) => mix($accent, '#ffffff', 0.7)};
    transform: translateX(-50%) rotate(45deg);
    border-radius: 2px;
  }
`;

/**
 * The icon carved into an available, unfinished coin.
 *
 * @param kind - The item's kind.
 * @return The matching icon element.
 */
function kindIcon(kind: ExpeditionItemKind): ReactNode {
  switch (kind) {
    case 'learn':
      return <LearnIcon />;
    case 'code':
      return <CodeIcon />;
    case 'math':
      return <FaRocket />;
    case 'section':
      return <SectionIcon />;
    case 'link':
      return <LinkIcon />;
    case 'check':
      return <CheckIcon />;
  }
}

/** Props for {@link ExpeditionRoadmap}. */
export interface ExpeditionRoadmapProps {
  /** Weeks in course order, each already marked locked or not. */
  weeks: readonly ExpeditionWeek[];
  /** Ids of completed items, across both tracks. */
  completed: ReadonlySet<string>;
  /** Called when an available coin or gem is pressed. */
  onSelect: (item: ExpeditionItem) => void;
  /**
   * The course's look and feel: titles, accents, placed widgets and global
   * switches. Defaults to the stock look.
   */
  lookAndFeel?: LookAndFeel;
  /**
   * Reports the map's geometry after each layout, for an editor that overlays
   * controls on the map. When omitted, nothing is reported.
   */
  onLayout?: (info: RoadmapLayoutInfo) => void;
  /**
   * Lets the viewer press the coins and gems of locked weeks, which keep
   * their locked look. Defaults to false: locked items are disabled.
   */
  canOpenLocked?: boolean;
}

/** The expedition world map. */
export function ExpeditionRoadmap({
  weeks,
  completed,
  onSelect,
  lookAndFeel = DEFAULT_LOOK_AND_FEEL,
  onLayout,
  canOpenLocked = false,
}: ExpeditionRoadmapProps) {
  const nextItem = findNextItem({weeks, completed});
  const linkCallout = useMapLinkCallout({onSelect});
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(CANVAS_WIDTH);
  useLayoutEffect(() => {
    const element = scrollerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }
    // Measured here, before the first paint, so the map is never drawn at
    // the placeholder width and then re-laid out. State set in a layout
    // effect is applied before the browser paints.
    if (element.clientWidth) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- measuring before paint is what a layout effect is for
      setContainerWidth(element.clientWidth);
    }
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width;
      if (width) {
        setContainerWidth(width);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const g = geometryFor(
    Math.max(
      CANVAS_WIDTH,
      Math.min(MAX_CANVAS_WIDTH, Math.floor(containerWidth)),
    ),
  );
  const scale = Math.min(1, containerWidth / CANVAS_WIDTH);
  const isFullBleed = containerWidth < FULL_BLEED_BELOW;
  const layout = layoutExpedition({
    weeks,
    nextItemId: nextItem?.id,
    g,
    lookAndFeel,
  });
  const lastLandIndex = layout.lands.length - 1;
  const layoutReport: RoadmapLayoutInfo = {
    width: layout.g.width,
    height: layout.height,
    scale,
    lands: layout.lands.map(land =>
      landBox(
        land,
        withoutHidden(
          autoPlacementsFor(land, completed, nextItem?.id),
          weekLook(lookAndFeel, land.week.id).hiddenAuto,
        ),
      ),
    ),
  };
  const layoutKey = JSON.stringify(layoutReport);
  useEffect(() => {
    onLayout?.(layoutReport);
    // Keyed on the report itself: re-sent whenever any of it changes, not on
    // every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKey, onLayout]);

  return (
    <Scroller ref={scrollerRef}>
      <Frame
        $width={layout.g.width * scale}
        $height={layout.height * scale}
        $isFullBleed={isFullBleed}
      >
        <Canvas $width={layout.g.width} $height={layout.height} $scale={scale}>
          <World aria-hidden>
            <defs>
              {/* Faint paper grain, one filter shared by every land. */}
              <filter id="expedition-grain" x="0" y="0" width="1" height="1">
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.9"
                  numOctaves={2}
                  stitchTiles="stitch"
                  result="noise"
                />
                <feColorMatrix
                  in="noise"
                  type="matrix"
                  values="0 0 0 0 0.35  0 0 0 0 0.3  0 0 0 0 0.25  0 0 0 0.16 0"
                />
              </filter>
              {layout.lands.map(land => {
                const {ground, accent} = land.palette;
                return (
                  <linearGradient
                    key={`sky-${land.week.id}`}
                    id={`sky-${land.week.id}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0" stopColor={lighten(ground, 0.5)} />
                    <stop offset="0.45" stopColor={ground} />
                    <stop offset="1" stopColor={mix(ground, accent, 0.1)} />
                  </linearGradient>
                );
              })}
            </defs>
            {layout.lands.map(land => {
              const shape = landPath(land, land.index === lastLandIndex);
              return (
                <g key={land.week.id}>
                  <path d={shape} fill={`url(#sky-${land.week.id})`} />
                  <path
                    d={shape}
                    filter="url(#expedition-grain)"
                    opacity={land.isLocked ? 0.5 : 1}
                  />
                  {/* Layered hills: three soft ridges, deeper toward the horizon. */}
                  {[0.1, 0.18, 0.27].map((depth, ridge) => (
                    <path
                      key={`ridge-${depth}`}
                      d={ridgePath(land, ridge)}
                      fill={mix(
                        land.palette.ground,
                        land.palette.accent,
                        depth,
                      )}
                    />
                  ))}
                  {svgDecorations({
                    placements: [
                      ...(lookAndFeel.showAutoScenery
                        ? withoutHidden(
                            autoSceneryPlacements(land, plushSideOf(land)),
                            weekLook(lookAndFeel, land.week.id).hiddenAuto,
                          )
                        : []),
                      ...weekLook(lookAndFeel, land.week.id).decorations,
                    ],
                    land: landBox(land, []),
                    width: layout.g.width,
                    isLocked: land.isLocked,
                  })}
                </g>
              );
            })}
            {/* Each land's horizon is redrawn over the previous land's hills. */}
            {layout.lands.map(land =>
              land.index === 0 ? null : (
                <path
                  key={`horizon-${land.week.id}`}
                  d={landPath(layout.lands[land.index - 1], false)}
                  fill="none"
                  stroke="rgba(255,255,255,0.65)"
                  strokeWidth={3}
                />
              ),
            )}
            {layout.lands.map(land => {
              if (land.stones.length === 0 || land.roadTo <= land.roadFrom) {
                return null;
              }
              // The road is worn in as far as the first unfinished coin.
              const firstOpen = land.stones.find(
                stone => !completed.has(stone.item.id),
              );
              const doneTo = land.isLocked
                ? undefined
                : firstOpen
                  ? firstOpen.y
                  : land.roadTo;
              const base = land.palette.road;
              const edge = mix(base, land.palette.accent, 0.18);
              const worn = mix(base, land.palette.accent, 0.42);
              const full = meanderPath(
                land.roadFrom,
                land.roadTo,
                layout.meander,
                land.anchors,
              );
              return (
                <g
                  key={`road-${land.week.id}`}
                  opacity={land.isLocked ? 0.55 : 1}
                >
                  <path
                    d={full}
                    fill="none"
                    stroke={edge}
                    strokeWidth={PATH_WIDTH + 3}
                    strokeLinecap="round"
                    opacity={0.5}
                  />
                  <path
                    d={full}
                    fill="none"
                    stroke={base}
                    strokeWidth={PATH_WIDTH}
                    strokeLinecap="round"
                  />
                  {doneTo !== undefined && doneTo > land.roadFrom && (
                    <path
                      d={meanderPath(
                        land.roadFrom,
                        doneTo,
                        layout.meander,
                        land.anchors,
                      )}
                      fill="none"
                      stroke={worn}
                      strokeWidth={PATH_WIDTH}
                      strokeLinecap="round"
                    />
                  )}
                </g>
              );
            })}
          </World>

          {layout.lands.map(land => {
            const {palette, week, isLocked} = land;
            const ink = isLocked
              ? mix(palette.accent, '#6f685f', 0.5)
              : darken(palette.accent, 0.4);
            const stoneColor = isLocked
              ? mix(palette.accent, LOCKED_STONE_COLOR, 0.55)
              : palette.accent;
            const doneCount = countDone(week.main, completed);
            const isLandDone = !isLocked && allDone(week.main, completed);
            return (
              <div key={week.id}>
                <Header $top={land.headerTop} $accent={palette.accent}>
                  <div>
                    <HeaderKicker>
                      {[week.label, week.date, week.instructor]
                        .filter(Boolean)
                        .join(' · ')}
                    </HeaderKicker>
                    <HeaderTitle>{land.title}</HeaderTitle>
                  </div>
                  <HeaderPill>
                    {isLocked ? (
                      <>
                        <LockIcon /> Locked
                      </>
                    ) : isLandDone ? (
                      <>
                        <TrophyIcon /> Complete
                      </>
                    ) : (
                      `${doneCount} / ${week.main.length}`
                    )}
                  </HeaderPill>
                </Header>

                <HtmlDecorations
                  placements={[
                    ...(lookAndFeel.showMascots
                      ? withoutHidden(
                          autoMascotPlacements(land, completed, nextItem?.id),
                          weekLook(lookAndFeel, week.id).hiddenAuto,
                        )
                      : []),
                    ...weekLook(lookAndFeel, week.id).decorations,
                  ]}
                  land={landBox(land, [])}
                  width={layout.g.width}
                  isLocked={isLocked}
                />

                {land.stones.map(stone => {
                  const isDone = !isLocked && completed.has(stone.item.id);
                  const isNext = stone.item.id === nextItem?.id;
                  const disclosure = linkCallout.disclosureFor(stone.item);
                  return (
                    <div key={stone.item.id} data-item-id={stone.item.id}>
                      <JourneyStone
                        x={stone.x}
                        y={stone.y}
                        size={STONE_SIZE}
                        color={stoneColor}
                        icon={
                          isLocked ? (
                            <LockIcon />
                          ) : isDone ? (
                            <CheckIcon />
                          ) : (
                            kindIcon(stone.item.kind)
                          )
                        }
                        finish="matte"
                        progress={
                          isNext
                            ? countDone(week.main, completed) /
                              Math.max(1, week.main.length)
                            : undefined
                        }
                        isLocked={isLocked}
                        canOpenLocked={canOpenLocked}
                        label={stone.item.title}
                        title={itemTooltip({
                          item: stone.item,
                          isLocked,
                          canOpenLocked,
                        })}
                        isPressed={
                          linkCallout.isCalloutLink(stone.item)
                            ? undefined
                            : isDone
                        }
                        isExpanded={disclosure['aria-expanded']}
                        controls={disclosure['aria-controls']}
                        onClick={event => linkCallout.select(stone.item, event)}
                      />
                      <CenterCaption
                        $x={stone.x}
                        $y={stone.y + STONE_SIZE / 2 + 12}
                        $color={ink}
                      >
                        {isNext && <small>Up next</small>}
                        <strong>{stone.item.title}</strong>
                      </CenterCaption>
                      {linkCallout.calloutFor({
                        item: stone.item,
                        centerX: stone.x,
                        top: stone.y + STONE_SIZE / 2 + 10,
                        boundsWidth: layout.g.width,
                      })}
                      {isNext && (
                        <>
                          <StartBubble
                            $x={stone.x}
                            $y={stone.y - STONE_SIZE / 2 - RING_GAP - 58}
                            $accent={palette.accent}
                          >
                            Start
                          </StartBubble>
                        </>
                      )}
                    </div>
                  );
                })}

                {land.shelf && (
                  <>
                    <Shelf
                      $left={land.shelf.left}
                      $top={land.shelf.top}
                      $width={land.shelf.width}
                      $height={land.shelf.height}
                      $accent={palette.accent}
                      $ground={palette.ground}
                    >
                      <ShelfLabel $color={ink}>
                        {lookAndFeel.goingFurtherLabel}
                      </ShelfLabel>
                    </Shelf>
                    {land.shelf.gems.map(gem => {
                      const isDone = !isLocked && completed.has(gem.item.id);
                      const isLink = linkCallout.isCalloutLink(gem.item);
                      const disclosure = linkCallout.disclosureFor(gem.item);
                      return (
                        <div key={gem.item.id}>
                          <JourneyStone
                            x={gem.x}
                            y={gem.y}
                            size={GEM_SIZE}
                            color={
                              isLocked
                                ? stoneColor
                                : mix(palette.accent, '#ffffff', 0.12)
                            }
                            icon={
                              isLocked ? (
                                <LockIcon />
                              ) : isDone ? (
                                <CheckIcon />
                              ) : isLink ? (
                                <LinkIcon />
                              ) : (
                                <GemIcon />
                              )
                            }
                            finish="matte"
                            isLocked={isLocked}
                            canOpenLocked={canOpenLocked}
                            isOptional={!isDone && !isLocked}
                            label={`Going further: ${gem.item.title}`}
                            title={itemTooltip({
                              item: gem.item,
                              isLocked,
                              canOpenLocked,
                            })}
                            isPressed={isLink ? undefined : isDone}
                            isExpanded={disclosure['aria-expanded']}
                            controls={disclosure['aria-controls']}
                            onClick={event =>
                              linkCallout.select(gem.item, event)
                            }
                          />
                          <UnderCaption
                            $x={gem.x}
                            $y={gem.y + GEM_SIZE / 2 + 10}
                            $color={ink}
                          >
                            {gem.item.title}
                          </UnderCaption>
                          {linkCallout.calloutFor({
                            item: gem.item,
                            centerX: gem.x,
                            top: gem.y + GEM_SIZE / 2 + 10,
                            boundsWidth: layout.g.width,
                          })}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            );
          })}
          {lookAndFeel.showFinale && (
            <MapFinale
              top={layout.finaleTop}
              inset={HEADER_MARGIN_X}
              title={lookAndFeel.finaleTitle}
              subtitle={lookAndFeel.finaleSubtitle}
            />
          )}
        </Canvas>
      </Frame>
    </Scroller>
  );
}
